import { useEffect } from 'react';
import axios, { AxiosResponse, isAxiosError } from 'axios';
import { useNavigate } from 'react-router-dom';
import { useServerVersionCheck } from 'Hooks/useServerVersionCheck';
import { IGNORED_SESSION_EXPIRY_URLS } from './AuthConstants';
import alertToast from 'Util/ToastifyUtils';
import { store } from 'Models/Store';

let handlingSessionExpiry = false;

export function handleSessionExpired(navigate: (path: string) => void) {
	if (handlingSessionExpiry) return;
	handlingSessionExpiry = true;

	const { location } = window;
	alertToast('Your session has expired. Please log in again.', 'error');
	store.clearLoggedInUser();
	const redirect = location.pathname + location.search;
	navigate(`/login?redirect=${encodeURIComponent(redirect)}`);

	// Reset after a short delay so future genuine expiries can trigger
	setTimeout(() => {
		handlingSessionExpiry = false;
	}, 2000);
}

export const SessionInterceptor = () => {
	const navigate = useNavigate();
	const { checkServerVersion } = useServerVersionCheck();

	useEffect(() => {
		const onResponse = (response: AxiosResponse): AxiosResponse => {
			const serverVersion = response.headers['x-version'];
			checkServerVersion(serverVersion);
			return response;
		};

		const interceptor = axios.interceptors.response.use(
			(res: AxiosResponse) => {
				const isIgnoredUrl = IGNORED_SESSION_EXPIRY_URLS.some(url => res.config?.url?.endsWith(url));
				if (res.status === 401 && !isIgnoredUrl) {
					handleSessionExpired(navigate);
				}
				return onResponse(res);
			},
			(errRes: any) => {
				if (isAxiosError(errRes)) {
					const isIgnoredUrl = IGNORED_SESSION_EXPIRY_URLS.some(url => errRes.config?.url?.endsWith(url));
					if (errRes.response?.status === 401 && !isIgnoredUrl) {
						handleSessionExpired(navigate);
					}
				}
				throw errRes;
			},
		);

		return () => {
			axios.interceptors.response.eject(interceptor);
		};
	}, [navigate, checkServerVersion]);

	return null;
};
